// ============================================================
// EXPOSURES - a status on a target that modifies whoever INTERACTS
// with that target (v8.349.0).
//
// A condition changes what its bearer can do: Stunned, Blinded, Prone.
// An EXPOSURE changes what everyone ELSE can do to its bearer: Painted
// (+20 to hit them), Breached (+3 penetration against them), Shrouded
// (−20 to shoot them), Terrifying (a Willpower test to attack them at
// all). The attacker reads it; the bearer never rolls against it.
//
// AN EXPOSURE IS A CODE, NOT A FIELD (ADR 2026-09-12). It lives in
// `state.conditions` as an `Ex:` string, the way `Pen:` and `Dn:` do,
// so sync, undo, the countdown, the token chips, the projection, the
// surfaces and the log all carry it for free - and it is shown in its
// own Exposures section, never among the conditions, and never judged
// by the condition immunity map (it modifies others, so nothing is
// immune to wearing one).
//
// THE GRAMMAR, one code = one exposure = one duration:
//
//   Ex:<name>;<axis>=<value>[,<axis>=<value>…][;@<scope>][;~<mode>][;#<uses>][;^<cap>][;radius=<m>][:<rounds>]
//
//   Ex:painted;hit=20:3            Painted, +20 to hit, 3 rounds
//   Ex:custom;bs=-20,dmg=2         a custom one, until removed
//   Ex:marked;hit=10,dmg=2;@hostile;#1   hostiles only, consumed by one hit
//   Ex:shocking;curse=S1;~melee    melee attackers who hit are Stunned
//
// `<name>` is a preset id or `custom`. Flag axes (`snap`, `ignoreTB`…)
// are bare words; an axis value is everything after the FIRST `=`, so a
// condition-valued axis may carry `=`, `+` and `:` (`afflict=Pen:dodge=-10`)
// - but never a trailing `:<digits>`, which the outer clock would claim.
// `@scope` (v8.368.0, STRICT) is `all` (default), `hostile` (a different
// team tag on both sides), `ally` (the same tag on both sides) or a team
// tag; somebody with no team is reached by `all` alone. `~melee` /
// `~ranged` restricts it to one kind of attack. `#N` is the number of
// landed hits that consume it. `^N` caps the axis total when several
// exposures stack on one axis. `radius=N` makes it an AURA read by
// whoever stands within N metres (v8.368.8). `:rounds` is the
// countdown's suffix, as for `Pen:` - absent means until removed.
//
// STACKING: the same code type refreshes (mergeCondition's rule for
// timed codes); different exposures on one axis SUM, clamped by the
// smallest cap any of them carries.
//
// Pure. Zero React dependency.
// ============================================================
import { effectReaches, sideKey } from './effectScope';
import { SIZE_ORDER, SIZE_MODS } from './constants';
import { splitClock } from './codeClock';

// ── Axes ──────────────────────────────────────────────────────

/** Numeric axes: a signed value. */
export type NumericAxis =
  | 'hit' // to-hit, melee and ranged
  | 'ws' // to-hit in melee only
  | 'bs' // to-hit at range only
  | 'size' // treat as N sizes larger (+) or smaller (−) to hit
  | 'cover' // attackers ignore up to N cover levels
  | 'range' // N range bands closer (−) or further (+), ranged only
  | 'dmg' // ± damage per hit
  | 'pen' // ± penetration
  | 'dos' // ± degrees of success on a hit
  | 'dread' // WP test at ± to attack at all
  | 'fury' // Righteous Fury triggers on N and up against them (v8.368.1; the old bare flag reads as 9)
  | 'crit' // ± critical severity of hits on them
  | 'tb' // ± their Toughness Bonus as the hit sees it (−2 = counts two less)
  | 'dmgCap' // no single hit deals more than N to them
  | 'hits' // ± hits a volley against them lands (never below one)
  | 'jam' // attackers' weapons jam on N and up against them
  | 'overheat' // attackers' weapons overheat on N and up against them - the Overheats rule (v8.371.1)
  | 'called' // the called-shot penalty against them is reduced by N (20 waives it) (v8.368.2)
  | 'charge' // ± to attacks made by charging them
  | 'rear' // ± to attacks arriving on their rear arc
  | 'maxRange' // they cannot be shot from beyond N metres
  | 'dodge' // ± to DODGING the bearer's own attacks (v8.368.3) - the bearer is the attacker
  | 'parry' // ± to PARRYING the bearer's own attacks
  | 'pin' // ± to the pinning tests the bearer's fire forces (v8.368.5) - the bearer is the shooter
  | 'luck' // +1: attackers may re-roll a miss against them; −1: they must re-roll a hit (v8.368.5)
  | 'escape' // ± to breaking free of the bearer's hold - their side of a grapple (v8.368.6)
  | 'initiative'; // ± to the initiative of whoever stands under the aura (v8.368.8) - aura only
/** Damage by type: `dmg:E`, `dmg:I`, `dmg:R`, `dmg:X`. */
export type TypedDamageAxis = `dmg:${'E' | 'I' | 'R' | 'X'}`;
/** Flag axes: present or not. */
export type FlagAxis =
  | 'snap'
  | 'ignoreTB'
  | 'ignoreNatural'
  | 'ignoreField'
  | 'taunt'
  | 'sanctioned'
  | 'autoHit' // attacks against them cannot miss (v8.368.2)
  | 'noMelee' // they cannot be reached in melee
  | 'noRanged'; // they cannot be shot
/** Expression axes: a damage expression with a type, e.g. `1d5R`. */
export type ExprAxis = 'retaliate' | 'retaliateMiss' | 'grasp';
/** Condition axes (v8.368.4): a TIMELESS condition code laid by a hit - on them (`afflict`, typed `afflict:E`), or on who hit them (`curse`). */
type CondAxis = 'afflict' | 'curse' | `afflict:${'E' | 'I' | 'R' | 'X'}`;
/** A hit location id (v8.368.5): every uncalled hit on them lands there. */
type LocAxis = 'hitLocation';
export type Axis = NumericAxis | TypedDamageAxis | FlagAxis | ExprAxis | CondAxis | LocAxis;

export interface AxisDef {
  id: Axis;
  label: string;
  /** What it does to whoever interacts with the bearer. */
  hint: string;
  /** `cond`: a timeless condition code; `loc`: a hit location id (v8.368.0). */
  kind: 'number' | 'flag' | 'expr' | 'cond' | 'loc';
  /** The family, for the picker's grouping. */
  family: 'to-hit' | 'on-hit' | 'hazard' | 'gating' | 'reaction' | 'aura';
}

export const AXES: readonly AxisDef[] = [
  { id: 'hit', label: 'To hit', hint: '± to every attack roll against them', kind: 'number', family: 'to-hit' },
  { id: 'ws', label: 'WS to hit', hint: '± to melee attacks against them', kind: 'number', family: 'to-hit' },
  { id: 'bs', label: 'BS to hit', hint: '± to ranged attacks against them', kind: 'number', family: 'to-hit' },
  {
    id: 'size',
    label: 'Size',
    hint: 'treat as N sizes larger (+) or smaller (−) to hit',
    kind: 'number',
    family: 'to-hit',
  },
  {
    id: 'cover',
    label: 'Cover denied',
    hint: 'attackers ignore up to N levels of their cover',
    kind: 'number',
    family: 'to-hit',
  },
  {
    id: 'range',
    label: 'Range bands',
    hint: 'N bands closer (−) or further (+) for ranged attacks',
    kind: 'number',
    family: 'to-hit',
  },
  { id: 'snap', label: 'No aiming', hint: 'attackers may not aim at them', kind: 'flag', family: 'to-hit' },
  {
    id: 'called',
    label: 'Called shots',
    hint: 'the called-shot penalty against them is reduced by N (20 = no penalty)',
    kind: 'number',
    family: 'to-hit',
  },
  { id: 'charge', label: 'Charged', hint: '± to attacks made by charging them', kind: 'number', family: 'to-hit' },
  {
    id: 'rear',
    label: 'From behind',
    hint: '± to attacks arriving on their rear arc (the map knows the bearing)',
    kind: 'number',
    family: 'to-hit',
  },
  {
    id: 'autoHit',
    label: 'Cannot miss',
    hint: 'attacks against them hit without a roll',
    kind: 'flag',
    family: 'to-hit',
  },
  {
    id: 'maxRange',
    label: 'Untargetable beyond',
    hint: 'they cannot be shot from beyond N metres',
    kind: 'number',
    family: 'gating',
  },
  { id: 'noMelee', label: 'Untouchable', hint: 'they cannot be reached in melee', kind: 'flag', family: 'gating' },
  { id: 'noRanged', label: 'Unshootable', hint: 'they cannot be shot at', kind: 'flag', family: 'gating' },
  {
    id: 'dodge',
    label: 'Dodging them',
    hint: '± to dodging THEIR attacks - Whirling −10, Lumbering +10',
    kind: 'number',
    family: 'reaction',
  },
  { id: 'parry', label: 'Parrying them', hint: '± to parrying THEIR attacks', kind: 'number', family: 'reaction' },
  {
    id: 'pin',
    label: 'Pinned by them',
    hint: '± to the pinning tests THEIR fire forces (Withering fire −10)',
    kind: 'number',
    family: 'reaction',
  },
  {
    id: 'escape',
    label: 'Breaking free of them',
    hint: '± to the other side of a grapple with them (Clinging −20, Slippery +20)',
    kind: 'number',
    family: 'reaction',
  },
  {
    id: 'initiative',
    label: 'Initiative near them',
    hint: '± to the initiative roll of whoever stands within the radius - an aura, nothing without one',
    kind: 'number',
    family: 'aura',
  },
  {
    id: 'luck',
    label: 'Luck',
    hint: '+1: attackers may re-roll a miss against them (Doomed); −1: they must re-roll a hit (Charmed)',
    kind: 'number',
    family: 'to-hit',
  },
  {
    id: 'hitLocation',
    label: 'Hits land on',
    hint: 'every uncalled hit on them lands on this location (head, body, leftArm…)',
    kind: 'loc',
    family: 'on-hit',
  },
  { id: 'dmg', label: 'Damage', hint: '± damage per hit that lands', kind: 'number', family: 'on-hit' },
  { id: 'dmg:E', label: 'Energy damage', hint: '± damage per Energy hit', kind: 'number', family: 'on-hit' },
  { id: 'dmg:I', label: 'Impact damage', hint: '± damage per Impact hit', kind: 'number', family: 'on-hit' },
  { id: 'dmg:R', label: 'Rending damage', hint: '± damage per Rending hit', kind: 'number', family: 'on-hit' },
  { id: 'dmg:X', label: 'Explosive damage', hint: '± damage per Explosive hit', kind: 'number', family: 'on-hit' },
  { id: 'pen', label: 'Penetration', hint: '± penetration against them', kind: 'number', family: 'on-hit' },
  { id: 'ignoreTB', label: 'Ignores TB', hint: 'hits bypass their Toughness Bonus', kind: 'flag', family: 'on-hit' },
  {
    id: 'ignoreNatural',
    label: 'Ignores natural armour',
    hint: 'hits bypass their natural armour',
    kind: 'flag',
    family: 'on-hit',
  },
  {
    id: 'ignoreField',
    label: 'Ignores force field',
    hint: 'hits are not stopped by their field',
    kind: 'flag',
    family: 'on-hit',
  },
  {
    id: 'fury',
    label: 'Fury on',
    hint: 'Righteous Fury triggers on this die face and up against them (9 = the classic 9-10)',
    kind: 'number',
    family: 'on-hit',
  },
  { id: 'dos', label: 'Degrees', hint: '± degrees of success on a hit against them', kind: 'number', family: 'on-hit' },
  {
    id: 'crit',
    label: 'Critical severity',
    hint: '± severity of critical hits on them',
    kind: 'number',
    family: 'on-hit',
  },
  {
    id: 'tb',
    label: 'Toughness Bonus',
    hint: '± their Toughness Bonus as a hit sees it (−2 = counts two less)',
    kind: 'number',
    family: 'on-hit',
  },
  {
    id: 'dmgCap',
    label: 'Damage cap',
    hint: 'no single hit deals more than N to them',
    kind: 'number',
    family: 'on-hit',
  },
  {
    id: 'hits',
    label: 'Hits',
    hint: '± hits a volley against them lands, never below one',
    kind: 'number',
    family: 'to-hit',
  },
  {
    id: 'jam',
    label: 'Jams on',
    hint: "attackers' weapons jam on this roll and up against them (ranged)",
    kind: 'number',
    family: 'to-hit',
  },
  {
    id: 'overheat',
    label: 'Overheats on',
    hint: "attackers' weapons overheat on this roll and up against them (ranged; the Overheats rule - drop it or take its fury)",
    kind: 'number',
    family: 'to-hit',
  },
  {
    id: 'retaliate',
    label: 'Retaliation',
    hint: 'melee attackers who HIT take this damage (e.g. 1d5R)',
    kind: 'expr',
    family: 'hazard',
  },
  {
    id: 'retaliateMiss',
    label: 'Retaliation on a miss',
    hint: 'melee attackers who MISS take this damage',
    kind: 'expr',
    family: 'hazard',
  },
  {
    id: 'grasp',
    label: 'Grasping',
    hint: 'melee attackers who hit test (S or Ag, ±N) or are Snared',
    kind: 'expr',
    family: 'hazard',
  },
  {
    id: 'afflict',
    label: 'Afflicted',
    hint: 'any hit on them lays this condition on THEM (a timeless code: S1, Fi, Pen:all=-5)',
    kind: 'cond',
    family: 'on-hit',
  },
  {
    id: 'afflict:E',
    label: 'Afflicted by Energy',
    hint: 'any Energy hit on them lays this condition on them',
    kind: 'cond',
    family: 'on-hit',
  },
  {
    id: 'afflict:I',
    label: 'Afflicted by Impact',
    hint: 'any Impact hit on them lays this condition on them',
    kind: 'cond',
    family: 'on-hit',
  },
  {
    id: 'afflict:R',
    label: 'Afflicted by Rending',
    hint: 'any Rending hit on them lays this condition on them',
    kind: 'cond',
    family: 'on-hit',
  },
  {
    id: 'afflict:X',
    label: 'Afflicted by Explosive',
    hint: 'any Explosive hit on them lays this condition on them',
    kind: 'cond',
    family: 'on-hit',
  },
  {
    id: 'curse',
    label: 'Curses',
    hint: 'whoever hits them wears this condition (a timeless code)',
    kind: 'cond',
    family: 'hazard',
  },
  {
    id: 'dread',
    label: 'Dread',
    hint: 'attackers must pass a Willpower test at ± to attack at all',
    kind: 'number',
    family: 'gating',
  },
  {
    id: 'taunt',
    label: 'Taunting',
    hint: 'enemies who can target them must - a reminder, not a rule',
    kind: 'flag',
    family: 'gating',
  },
  { id: 'sanctioned', label: 'Sanctioned', hint: 'allies cannot target them', kind: 'flag', family: 'gating' },
];

const AXIS_IDS = new Set<string>(AXES.map((a) => a.id));

// ── The record ────────────────────────────────────────────────

export type ExposureScope = 'all' | 'hostile' | 'ally' | (string & {});
export type ExposureMode = 'melee' | 'ranged';

export interface Exposure {
  /** A preset id, or 'custom'. */
  name: string;
  /** Numeric axes carry numbers, flags carry `true`, expressions carry their text. */
  axes: Partial<Record<Axis, number | true | string>>;
  scope: ExposureScope;
  /** Only against one kind of attack (v8.368.0); absent = both. */
  mode?: ExposureMode;
  /** An AURA: read by whoever stands within this many metres (v8.368.8); absent = only who interacts. */
  radius?: number;
  /** Landed hits that consume it; absent = not consumed by hits. */
  uses?: number;
  /** Clamp for the axis total when several stack; absent = uncapped. */
  cap?: number;
  /** 0 = until removed. */
  rounds: number;
}

const EX_PREFIX = 'Ex:';

/** An axis value from the text after its `=` (null when there was none), or null when malformed. */
type AxisValueParser = (id: Axis, raw: string | null) => number | true | string | null;

const AXIS_VALUE_PARSERS: Record<AxisDef['kind'], AxisValueParser> = {
  flag: () => true,
  number: (id, raw) => {
    // `fury` was a bare flag before v8.368.1, meaning "on a 9 or 10".
    if (raw === null) return id === 'fury' ? 9 : null;
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? null : n;
  },
  // A condition value is TIMELESS: a trailing `:N` on the whole code is
  // the exposure's own clock and was taken by the outer parse already.
  expr: (_, raw) => raw || null,
  cond: (_, raw) => raw || null,
  loc: (_, raw) => raw || null,
};

/** One `<axis>[=<value>]` item, or null when the axis is unknown or its value malformed. */
function parseAxisItem(item: string): [Axis, number | true | string] | null {
  // The FIRST `=` ends the id: a condition-valued axis keeps its own `=`.
  const eq = item.indexOf('=');
  const id = (eq >= 0 ? item.slice(0, eq) : item) as Axis;
  if (!AXIS_IDS.has(id)) return null;
  const kind = AXES.find((a) => a.id === id)!.kind;
  const value = AXIS_VALUE_PARSERS[kind](id, eq < 0 ? null : item.slice(eq + 1));
  return value === null ? null : [id, value];
}

/** The comma list of axes; null when any item is malformed or none is given. */
function parseAxes(axesText: string): Exposure['axes'] | null {
  const axes: Exposure['axes'] = {};
  for (const item of axesText.split(',')) {
    if (!item) continue;
    const parsed = parseAxisItem(item);
    if (!parsed) return null;
    axes[parsed[0]] = parsed[1];
  }
  return Object.keys(axes).length === 0 ? null : axes;
}

/** The `;`-separated options after the axes. */
type ExposureOptions = Pick<Exposure, 'scope' | 'mode' | 'radius' | 'uses' | 'cap'>;

/** Each option's marker and what it sets; a part no marker claims makes the code malformed. */
const OPTION_PARSERS: readonly [(part: string) => boolean, (opts: ExposureOptions, part: string) => void][] = [
  [(p) => p.startsWith('@'), (o, p) => (o.scope = p.slice(1) || 'all')],
  [(p) => p === '~melee' || p === '~ranged', (o, p) => (o.mode = p.slice(1) as ExposureMode)],
  [(p) => p.startsWith('radius='), (o, p) => (o.radius = parseInt(p.slice(7), 10) || undefined)],
  [(p) => p.startsWith('#'), (o, p) => (o.uses = parseInt(p.slice(1), 10) || undefined)],
  [(p) => p.startsWith('^'), (o, p) => (o.cap = parseInt(p.slice(1), 10) || undefined)],
];

/** The options, or null when a part is not one. */
function parseOptions(parts: string[]): ExposureOptions | null {
  const opts: ExposureOptions = { scope: 'all' };
  for (const part of parts) {
    const parser = OPTION_PARSERS.find(([claims]) => claims(part));
    if (!parser) return null;
    parser[1](opts, part);
  }
  return opts;
}

/** The optional fields a parsed exposure carries only when set. */
const OPTIONAL_FIELDS = ['mode', 'radius', 'uses', 'cap'] as const;

/** The exposure with each optional field the options set (and only those), in their fixed order. */
function withOptionalFields(ex: Exposure, opts: ExposureOptions): Exposure {
  for (const field of OPTIONAL_FIELDS) if (opts[field]) Object.assign(ex, { [field]: opts[field] });
  return ex;
}

/** An exposure from its `;`-separated parts (name, axes, options) and its clock; null when malformed. */
function parseParts(parts: string[], rounds: number): Exposure | null {
  const [name, axesText, ...optionParts] = parts;
  if (!/^[a-z][a-z0-9-]*$/.test(name) || axesText === undefined) return null;
  const axes = parseAxes(axesText);
  const opts = axes && parseOptions(optionParts);
  if (!axes || !opts) return null;
  return withOptionalFields({ name, axes, scope: opts.scope, rounds }, opts);
}

/** Parse an `Ex:` code. Null for anything else, or a malformed one. */
export function parseExposure(code: string): Exposure | null {
  if (!code.startsWith(EX_PREFIX)) return null;
  const text = code.slice(EX_PREFIX.length);
  const clock = splitClock(text);
  return clock ? parseParts(clock.body.split(';'), clock.rounds) : parseParts(text.split(';'), 0);
}

/** `text` when `present` is truthy, else nothing. */
const when = (present: unknown, text: string): string => (present ? text : '');

/** The code for an exposure - the inverse of `parseExposure`. */
export function buildExposureCode(ex: Exposure): string {
  const axes = (Object.entries(ex.axes) as [Axis, number | true | string][])
    .map(([id, v]) => (v === true ? id : `${id}=${v}`))
    .join(',');
  return (
    `${EX_PREFIX}${ex.name};${axes}` +
    when(ex.scope && ex.scope !== 'all', `;@${ex.scope}`) +
    when(ex.mode, `;~${ex.mode}`) +
    when(ex.uses, `;#${ex.uses}`) +
    when(ex.cap, `;^${ex.cap}`) +
    when(ex.radius, `;radius=${ex.radius}`) +
    when(ex.rounds > 0, `:${ex.rounds}`)
  );
}

export function isExposureCode(code: string): boolean {
  return code.startsWith(EX_PREFIX);
}

// ── Presets ───────────────────────────────────────────────────

export interface ExposurePreset {
  id: string;
  label: string;
  hint: string;
  axes: Exposure['axes'];
  /** Which way it cuts for the attacker, for the chip's colour. */
  helps: boolean;
  /** An aura preset (v8.368.8): its scope and radius are part of what it IS, not options. */
  scope?: ExposureScope;
  radius?: number;
}

export const EXPOSURE_PRESETS: readonly ExposurePreset[] = [
  { id: 'painted', label: 'Painted', hint: 'a marker beacon on them - +20 to hit', axes: { hit: 20 }, helps: true },
  {
    id: 'marked',
    label: 'Marked',
    hint: 'marked for death - +10 to hit, +2 damage',
    axes: { hit: 10, dmg: 2 },
    helps: true,
  },
  {
    id: 'shrouded',
    label: 'Shrouded',
    hint: 'smoke, a holo-field - −20 to shoot them',
    axes: { bs: -20 },
    helps: false,
  },
  { id: 'displaced', label: 'Displaced', hint: 'a displacer field - −20 to hit', axes: { hit: -20 }, helps: false },
  {
    id: 'breached',
    label: 'Breached',
    hint: 'armour cracked - +3 penetration against them',
    axes: { pen: 3 },
    helps: true,
  },
  {
    id: 'silhouetted',
    label: 'Silhouetted',
    hint: 'against the light - one size larger to hit',
    axes: { size: 1 },
    helps: true,
  },
  {
    id: 'compact',
    label: 'Compact',
    hint: 'the Black Carapace - one size smaller to hit',
    axes: { size: -1 },
    helps: false,
  },
  {
    id: 'flushed',
    label: 'Flushed out',
    hint: 'driven into the open - attackers ignore one level of cover',
    axes: { cover: 1 },
    helps: true,
  },
  {
    id: 'illuminated',
    label: 'Illuminated',
    hint: 'lit by a flare - one range band closer',
    axes: { range: -1 },
    helps: true,
  },
  {
    id: 'flickering',
    label: 'Flickering',
    hint: 'never quite where they seem - no aiming at them',
    axes: { snap: true },
    helps: false,
  },
  {
    id: 'spined',
    label: 'Spined',
    hint: 'melee attackers who hit take 1d5 Rending',
    axes: { retaliate: '1d5R' },
    helps: false,
  },
  {
    id: 'burning',
    label: 'Burning',
    hint: 'melee attackers who hit take 1d10 Energy',
    axes: { retaliate: '1d10E' },
    helps: false,
  },
  {
    id: 'disturbing',
    label: 'Disturbing',
    hint: 'Fear 1 - a Willpower test to attack',
    axes: { dread: 0 },
    helps: false,
  },
  {
    id: 'frightening',
    label: 'Frightening',
    hint: 'Fear 2 - a Willpower test at −10 to attack',
    axes: { dread: -10 },
    helps: false,
  },
  {
    id: 'horrifying',
    label: 'Horrifying',
    hint: 'Fear 3 - a Willpower test at −20 to attack',
    axes: { dread: -20 },
    helps: false,
  },
  {
    id: 'terrifying',
    label: 'Terrifying',
    hint: 'Fear 4 - a Willpower test at −30 to attack',
    axes: { dread: -30 },
    helps: false,
  },
  // ── v8.368.7: the wider set ──────────────────────────────────
  {
    id: 'exposed-joints',
    label: 'Exposed joints',
    hint: 'no called-shot penalty against them',
    axes: { called: 20 },
    helps: true,
  },
  {
    id: 'weak-spot',
    label: 'Weak spot',
    hint: 'called shots at half the penalty, and they cut deeper (+1 crit)',
    axes: { called: 10, crit: 1 },
    helps: true,
  },
  {
    id: 'held-fast',
    label: 'Held fast',
    hint: 'attacks against them cannot miss',
    axes: { autoHit: true },
    helps: true,
  },
  { id: 'veiled', label: 'Veiled', hint: 'cannot be shot from beyond 20 m', axes: { maxRange: 20 }, helps: false },
  {
    id: 'untouchable',
    label: 'Untouchable',
    hint: 'cannot be reached in melee',
    axes: { noMelee: true },
    helps: false,
  },
  { id: 'void-shielded', label: 'Void-shielded', hint: 'cannot be shot at', axes: { noRanged: true }, helps: false },
  {
    id: 'insubstantial',
    label: 'Insubstantial',
    hint: 'a volley against them lands one hit fewer',
    axes: { hits: -1 },
    helps: false,
  },
  {
    id: 'brittle',
    label: 'Brittle',
    hint: 'critical hits on them are two severities deeper',
    axes: { crit: 2 },
    helps: true,
  },
  {
    id: 'hardy',
    label: 'Hardy',
    hint: 'critical hits on them are two severities shallower',
    axes: { crit: -2 },
    helps: false,
  },
  {
    id: 'volatile',
    label: 'Volatile',
    hint: 'Righteous Fury triggers on an 8 and up against them',
    axes: { fury: 8 },
    helps: true,
  },
  { id: 'soft', label: 'Soft', hint: 'their Toughness Bonus counts two less', axes: { tb: -2 }, helps: true },
  {
    id: 'sacred-ward',
    label: 'Sacred ward',
    hint: 'no single hit deals more than 8 to them',
    axes: { dmgCap: 8 },
    helps: false,
  },
  {
    id: 'soaked',
    label: 'Soaked in promethium',
    hint: 'any Energy hit sets them ablaze',
    axes: { 'afflict:E': 'Fi' },
    helps: true,
  },
  {
    id: 'glass-boned',
    label: 'Glass-boned',
    hint: 'any hit stuns them for a round',
    axes: { afflict: 'S1' },
    helps: true,
  },
  {
    id: 'shocking',
    label: 'Shocking',
    hint: 'whoever hits them is Stunned for a round',
    axes: { curse: 'S1' },
    helps: false,
  },
  {
    id: 'venomous',
    label: 'Venomous',
    hint: 'whoever hits them suffers Blood Loss',
    axes: { curse: 'BL' },
    helps: false,
  },
  {
    id: 'cursed',
    label: 'Cursed',
    hint: 'whoever hits them takes −10 to every test',
    axes: { curse: 'Pen:all=-10' },
    helps: false,
  },
  {
    id: 'corrosive',
    label: 'Corrosive',
    hint: "attackers' weapons jam on a 90 and up against them",
    axes: { jam: 90 },
    helps: false,
  },
  {
    id: 'sweltering',
    label: 'Sweltering',
    hint: "attackers' weapons overheat on a 90 and up against them - drop it or take its fury",
    axes: { overheat: 90 },
    helps: false,
  },
  { id: 'lumbering', label: 'Lumbering', hint: 'their attacks are +10 to dodge', axes: { dodge: 10 }, helps: false },
  { id: 'blurring', label: 'Blurring', hint: 'their attacks are −20 to dodge', axes: { dodge: -20 }, helps: true },
  { id: 'ponderous', label: 'Ponderous', hint: 'their attacks are +10 to parry', axes: { parry: 10 }, helps: false },
  { id: 'whirling', label: 'Whirling', hint: 'their attacks are −10 to parry', axes: { parry: -10 }, helps: true },
  {
    id: 'withering-fire',
    label: 'Withering fire',
    hint: 'the pinning tests their fire forces are at −10',
    axes: { pin: -10 },
    helps: true,
  },
  { id: 'braced', label: 'Braced', hint: 'charging them is −20', axes: { charge: -20 }, helps: false },
  { id: 'skittish', label: 'Skittish', hint: 'charging them is +10', axes: { charge: 10 }, helps: true },
  { id: 'blind-spot', label: 'Blind spot', hint: 'attacks from behind them are +20', axes: { rear: 20 }, helps: true },
  {
    id: 'doomed',
    label: 'Doomed',
    hint: 'a missed attack against them may be re-rolled',
    axes: { luck: 1 },
    helps: true,
  },
  { id: 'charmed', label: 'Charmed', hint: 'a hit on them must be re-rolled', axes: { luck: -1 }, helps: false },
  {
    id: 'hull-down',
    label: 'Hull down',
    hint: 'every uncalled hit on them lands on the body',
    axes: { hitLocation: 'body' },
    helps: false,
  },
  { id: 'clinging', label: 'Clinging', hint: 'breaking free of them is −20', axes: { escape: -20 }, helps: false },
  { id: 'slippery', label: 'Slippery', hint: 'breaking free of them is +20', axes: { escape: 20 }, helps: true },
  // ── v8.368.8: auras - read by whoever stands within the radius ──
  {
    id: 'awe',
    label: 'Awe',
    hint: 'hostiles within 6 m must pass a Willpower test at −10 to attack at all',
    axes: { dread: -10 },
    helps: false,
    scope: 'hostile',
    radius: 6,
  },
  {
    id: 'rallying',
    label: 'Rallying',
    hint: 'allies within 10 m are +10 to hit',
    axes: { hit: 10 },
    helps: true,
    scope: 'ally',
    radius: 10,
  },
];

/** The Fear ladder, by rating: 1 Disturbing, 2 Frightening, 3 Horrifying, 4 Terrifying. */
export const FEAR_PRESETS: readonly string[] = ['disturbing', 'frightening', 'horrifying', 'terrifying'];

function presetById(id: string): ExposurePreset | undefined {
  return EXPOSURE_PRESETS.find((p) => p.id === id);
}

/** A preset's code, with an optional duration and scope. */
export function presetCode(id: string, opts: { rounds?: number; scope?: ExposureScope; uses?: number } = {}): string {
  const p = presetById(id);
  if (!p) throw new Error(`unknown exposure preset: ${id}`);
  return buildExposureCode({
    name: p.id,
    axes: { ...p.axes },
    // An aura preset carries its own scope; the picker's applies only to the rest.
    scope: p.scope ?? opts.scope ?? 'all',
    rounds: opts.rounds ?? 0,
    ...(p.radius ? { radius: p.radius } : {}),
    ...(opts.uses ? { uses: opts.uses } : {}),
  });
}

// ── Reading ───────────────────────────────────────────────────

function fmtSigned(n: number): string {
  return n >= 0 ? `+${n}` : `−${Math.abs(n)}`;
}

/** A threshold or a ceiling carries no sign: "Fury on 8", "Jams on 90", "Damage cap 5". */
const UNSIGNED_AXES = new Set<Axis>(['fury', 'jam', 'overheat', 'dmgCap']);

/** One axis as a custom exposure's label shows it. */
function axisLabel(id: Axis, v: number | true | string): string {
  const def = AXES.find((a) => a.id === id)!;
  if (v === true) return def.label;
  if (typeof v === 'number') return `${def.label} ${UNSIGNED_AXES.has(id) ? v : fmtSigned(v)}`;
  return `${def.label} ${v}`;
}

/** 'Painted' for a preset, 'Exposed (BS −20, Dmg +2)' for a custom one. */
export function exposureLabel(ex: Exposure): string {
  const p = presetById(ex.name);
  if (p) return p.label;
  const parts = (Object.entries(ex.axes) as [Axis, number | true | string][]).map(([id, v]) => axisLabel(id, v));
  return `Exposed (${parts.join(', ')})`;
}

/** Singular or plural: '1 round', '3 rounds'. */
const counted = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/** Who a scope reaches, in words; null for everybody. */
function scopeNote(scope: ExposureScope): string | null {
  if (scope === 'hostile') return 'hostiles';
  if (scope === 'ally') return 'allies';
  return scope === 'all' ? null : `team ${scope}`;
}

/** The parenthesised notes of a decoded exposure: clock, scope, mode, radius, uses, stacking limit. */
function exposureNotes(ex: Exposure): string[] {
  return [
    ex.rounds > 0 ? counted(ex.rounds, 'round', 'rounds') : null,
    scopeNote(ex.scope),
    ex.mode ? `vs ${ex.mode}` : null,
    ex.radius ? `within ${ex.radius} m` : null,
    ex.uses ? counted(ex.uses, 'hit', 'hits') : null,
    // The cap clamps every stacked axis total it joins (`axisTotal`, `cappedToHit`); it went unsaid until 2026-10-20.
    ex.cap !== undefined ? `at most \u00b1${ex.cap} stacked` : null,
  ].filter((n): n is string => n !== null);
}

/** The decoded line: 'Painted (3 rounds, hostiles)', 'Marked (1 hit)'. */
export function describeExposure(code: string): string | null {
  const ex = parseExposure(code);
  if (!ex) return null;
  const notes = exposureNotes(ex);
  return notes.length ? `${exposureLabel(ex)} (${notes.join(', ')})` : exposureLabel(ex);
}

const asIs = (v: number) => v;
const against = (v: number) => -v;
const aHindrance = () => -1;

/**
 * What a numeric axis is worth to an attacker, where the plain sign is not
 * the answer. Unlisted axes count as they read (+ helps).
 */
const NUMERIC_AXIS_SCORES = new Map<Axis, (v: number) => number>([
  ['cover', asIs],
  ['crit', asIs],
  ['hits', asIs],
  ['called', asIs],
  ['range', against],
  ['tb', against],
  ['fury', (v) => Math.max(0, 10 - v)],
  ['dmgCap', aHindrance],
  ['jam', aHindrance],
  ['overheat', aHindrance],
  ['maxRange', aHindrance],
  // Reactions against THEIR attacks: an easier dodge helps everyone but them.
  ['dodge', against],
  ['parry', against],
  // Their fire pins harder at −N: like their dodge and parry, a gain for THEM - so scored as the
  // Withering fire preset reads it (the stage 4 review: a custom pin coloured opposite its preset).
  ['pin', against],
  // Their hold is harder to break at −N: read by the one trying to break it, so as it stands.
  ['escape', asIs],
]);

/** The flags that make them easier to hurt; every other flag makes it harder. */
const HELPFUL_FLAGS = new Set<Axis>(['ignoreTB', 'ignoreNatural', 'ignoreField', 'autoHit']);

/** One axis's weight in `exposureHelpsAttacker`. */
function axisScore(id: Axis, v: number | true | string): number {
  if (typeof v === 'number') return (NUMERIC_AXIS_SCORES.get(id) ?? asIs)(v);
  if (v === true) return HELPFUL_FLAGS.has(id) ? 1 : -1;
  // An affliction on them helps whoever hits them; a curse, a hazard, does not.
  return id.startsWith('afflict') ? 1 : -1;
}

/** Does the exposure make the attacker's life easier, on balance? For the chip's colour. */
export function exposureHelpsAttacker(ex: Exposure): boolean {
  const p = presetById(ex.name);
  if (p) return p.helps;
  const entries = Object.entries(ex.axes) as [Axis, number | true | string][];
  return entries.reduce((score, [id, v]) => score + axisScore(id, v), 0) > 0;
}

/** Flags that draw harm onto whoever wears them, whatever they do to the attacker's roll. */
const BAD_FOR_BEARER = new Set<Axis>(['taunt']);

/**
 * The axes read when the BEARER acts (v8.368.3): those facing his attacks dodge or parry them, his fire pins. Not
 * `escape`: that is read by the one trying to break HIS hold, like every axis aimed at the bearer.
 */
const BEARER_ACTS = new Set<Axis>(['dodge', 'parry', 'pin']);
const bearerAttacks = (ex: Exposure) => {
  const ids = Object.keys(ex.axes) as Axis[];
  return ids.length > 0 && ids.every((id) => BEARER_ACTS.has(id));
};

/**
 * IS IT GOOD FOR WHOEVER WEARS IT? (2026-10-23: exposure chips read from the bearer - green good for them, amber
 * bad.) An aura is read by those near: good when it helps allies or hinders hostiles. The reaction family is read when
 * the bearer attacks, so helping "the attacker" helps them. Everything else is read by whoever attacks the bearer,
 * so helping the attacker is bad for them - and Taunt, which draws fire, is bad whatever it scores.
 */
export function exposureGoodForBearer(ex: Exposure): boolean {
  const helps = exposureHelpsAttacker(ex);
  if (ex.radius) return ex.scope === 'hostile' ? !helps : helps;
  if (Object.keys(ex.axes).some((id) => BAD_FOR_BEARER.has(id as Axis))) return false;
  return bearerAttacks(ex) ? helps : !helps;
}

// ── Applying ──────────────────────────────────────────────────

/** Who is reading the exposure, and where from. */
export interface ExposureReader {
  /** The attacker's team tag, if any - for the `@<team>` scope, and `@hostile`/`@ally` without a side. */
  teamTag?: string | null;
  /** The attacker's SIDE (2026-10-23, `sideOf`), for `@hostile`, `@ally`, sanctioned and taunt; absent = the tag. */
  sideTag?: string | null;
  isMelee: boolean;
  /** The attack's range band, for the `range` axis. */
  rangeBand?: string | null;
  /** The attack is a charge, for the `charge` axis (v8.368.2). */
  charge?: boolean;
  /** Metres between the two, for `maxRange` (v8.368.2); null = the map cannot say. */
  distanceM?: number | null;
  /**
   * THE AURAS THE READER STANDS UNDER (v8.368.8): codes with a radius,
   * gathered by proximity and already scoped (`aurasByProximity`). Read
   * as if the reader's own target wore them, whoever that is - a
   * Rallying banner is +10 to hit anyone. `~mode` still applies.
   */
  auras?: readonly string[];
}

/** What the bearer looks like: what the scope compares against, what the size shift anchors on. */
export interface ExposureBearer {
  conditions?: readonly string[];
  /** INNATE exposures from the sheet (Fear (X), the Black Carapace) - never tick, never clear (v8.354.0). */
  exposures?: readonly string[];
  teamTag?: string | null;
  /** The bearer's SIDE (2026-10-23); absent = the tag. */
  sideTag?: string | null;
  size?: string | null;
  /** The arc the attack arrives on ('front', 'left', 'right', 'rear'), for the `rear` axis (v8.368.2). */
  arc?: string | null;
}

const tagOf = (t: string | null | undefined) => (t && t.trim() ? t.trim() : null);

/** Who counts as whose ally: the side, else the tag (an old payload). */
const sideOfParty = (p: { teamTag?: string | null; sideTag?: string | null }) => sideKey(p.sideTag, p.teamTag);

/**
 * Does this exposure apply to this attacker? Teams are STRICT (v8.368.0):
 * `hostile` needs a different tag on both sides and `ally` the same one;
 * somebody with no team is nobody's ally and nobody's hostile. `~mode`
 * then asks what kind of attack this is.
 */
export function exposureApplies(ex: Exposure, bearer: ExposureBearer, reader: ExposureReader): boolean {
  return modeApplies(ex, reader) && scopeApplies(ex, bearer, reader);
}

/** The `~mode` half alone: is this the kind of attack it restricts itself to? */
function modeApplies(ex: Exposure, reader: Pick<ExposureReader, 'isMelee'>): boolean {
  if (ex.mode === 'melee' && !reader.isMelee) return false;
  if (ex.mode === 'ranged' && reader.isMelee) return false;
  return true;
}

/** The `@scope` half alone - what an aura is gathered by, before any attack is known. */
export function scopeApplies(
  ex: Exposure,
  bearer: Pick<ExposureBearer, 'teamTag' | 'sideTag'>,
  reader: Pick<ExposureReader, 'teamTag' | 'sideTag'>,
): boolean {
  if (ex.scope === 'all') return true;
  if (ex.scope === 'hostile') return effectReaches('hostiles', sideOfParty(bearer), sideOfParty(reader));
  if (ex.scope === 'ally') return effectReaches('allies', sideOfParty(bearer), sideOfParty(reader));
  // A named team is that team alone - an allied team on its side is not it.
  const r = tagOf(reader.teamTag);
  return !!r && r === ex.scope;
}

/**
 * Every exposure the bearer wears that applies to the reader, parsed -
 * plus the auras the reader stands under (v8.368.8). A code WITH a
 * radius is an aura: it is never read off the bearer directly, only by
 * proximity through `reader.auras`, whose scope was settled when they
 * were gathered.
 */
export function activeExposures(bearer: ExposureBearer, reader: ExposureReader): Exposure[] {
  const out: Exposure[] = [];
  for (const code of [...(bearer.exposures ?? []), ...(bearer.conditions ?? [])]) {
    const ex = parseExposure(code);
    if (ex && !ex.radius && exposureApplies(ex, bearer, reader)) out.push(ex);
  }
  for (const code of reader.auras ?? []) {
    const ex = parseExposure(code);
    if (ex && modeApplies(ex, reader)) out.push(ex);
  }
  return out;
}

/** The smallest cap among the exposures, or undefined when none carries one. */
function smallestCap(exposures: readonly Exposure[]): number | undefined {
  const caps = exposures.map((ex) => ex.cap).filter((c): c is number => c !== undefined);
  return caps.length ? Math.min(...caps) : undefined;
}

/**
 * The numeric axis total across every applying exposure, capped by the
 * smallest cap any of them carries. Flag axes are true if any says so.
 */
export function axisTotal(exposures: readonly Exposure[], axis: NumericAxis | TypedDamageAxis): number {
  const bearing = exposures.filter((ex) => typeof ex.axes[axis] === 'number');
  const total = bearing.reduce((n, ex) => n + (ex.axes[axis] as number), 0);
  const cap = smallestCap(bearing);
  return cap === undefined ? total : Math.max(-cap, Math.min(cap, total));
}

export function axisFlag(exposures: readonly Exposure[], axis: FlagAxis): boolean {
  return exposures.some((ex) => ex.axes[axis] === true);
}

export function axisExprs(exposures: readonly Exposure[], axis: ExprAxis): { name: string; expr: string }[] {
  return exposures.flatMap((ex) =>
    typeof ex.axes[axis] === 'string' ? [{ name: exposureLabel(ex), expr: ex.axes[axis] as string }] : [],
  );
}

/**
 * The to-hit delta the SIZE axis is worth: the difference on the size
 * ladder between the shifted rung and the real one, clamped at the
 * ladder's ends. Black Carapace (−1 from Hulking) is −10; Silhouetted
 * (+1) on a Massive thing is 0 - there is no larger.
 */
export function sizeShiftToHit(size: string | null | undefined, steps: number): number {
  if (!steps) return 0;
  const idx = Math.max(0, SIZE_ORDER.indexOf(size || 'Average'));
  const shifted = Math.max(0, Math.min(SIZE_ORDER.length - 1, idx + steps));
  return (SIZE_MODS[SIZE_ORDER[shifted]] ?? 0) - (SIZE_MODS[SIZE_ORDER[idx]] ?? 0);
}

/** The range bands, nearest first, for the `range` axis. Kept here with the mods it moves between. */
const RANGE_BAND_ORDER: readonly string[] = ['pointBlank', 'short', 'normal', 'long', 'extreme'];
const RANGE_BAND_MODS: Record<string, number> = { pointBlank: 30, short: 10, normal: 0, long: -10, extreme: -30 };

/** The to-hit delta the RANGE axis is worth against the band the attack is at. Ranged only. */
export function rangeShiftToHit(rangeBand: string | null | undefined, steps: number): number {
  if (!steps || !rangeBand) return 0;
  const idx = RANGE_BAND_ORDER.indexOf(rangeBand);
  if (idx < 0) return 0;
  const shifted = Math.max(0, Math.min(RANGE_BAND_ORDER.length - 1, idx + steps));
  return (RANGE_BAND_MODS[RANGE_BAND_ORDER[shifted]] ?? 0) - (RANGE_BAND_MODS[rangeBand] ?? 0);
}

export interface ToHitExposure {
  /** The signed total against this attacker. */
  total: number;
  /** One named entry per applying exposure's to-hit contribution, for the ledger and the breakdown. */
  entries: { label: string; value: number }[];
  /** `snap`: no aiming at them. */
  noAim: boolean;
  /** `called` (v8.368.2): how much of the called-shot penalty they waive, 0-20. */
  calledShotReduction: number;
}

/** A numeric axis value, or 0 when the axis is absent (or not a number). */
const num = (v: number | true | string | undefined): number => (typeof v === 'number' ? v : 0);

/** One exposure's to-hit worth against this attack. */
function toHitValue(ex: Exposure, bearer: ExposureBearer, reader: ExposureReader): number {
  const a = ex.axes;
  let value = num(a.hit) + num(reader.isMelee ? a.ws : a.bs) + sizeShiftToHit(bearer.size, num(a.size));
  if (!reader.isMelee) value += rangeShiftToHit(reader.rangeBand, num(a.range));
  // The action and the bearing (v8.368.2): a charge, an attack from behind.
  if (reader.charge) value += num(a.charge);
  if (bearer.arc === 'rear') value += num(a.rear);
  return value;
}

/**
 * Clamp the summed to-hit total to the exposures' smallest cap, recording
 * the clamp as its own entry. The cap applies to the summed hit axis; the
 * size and range deltas are consequences of the ladder, not stackable
 * numbers.
 */
function cappedToHit(entries: { label: string; value: number }[], active: readonly Exposure[]): number {
  const total = entries.reduce((n, e) => n + e.value, 0);
  const cap = smallestCap(active);
  if (cap === undefined || Math.abs(total) <= cap) return total;
  const clamped = Math.sign(total) * cap;
  entries.push({ label: `Exposure cap ±${cap}`, value: clamped - total });
  return clamped;
}

/**
 * THE TO-HIT FAMILY, read by `getAttackConditionModifiers` - the one
 * funnel the roll, the live ledger and both forecasts already share, so
 * an exposure reaches all four by construction. Labels are PLAIN: every
 * renderer appends the value (the v8.237.4 lesson).
 */
export function toHitExposure(bearer: ExposureBearer, reader: ExposureReader): ToHitExposure {
  const active = activeExposures(bearer, reader);
  const entries: { label: string; value: number }[] = [];
  for (const ex of active) {
    const value = toHitValue(ex, bearer, reader);
    // An aura is not something the target wears: 'Near Rallying', not 'Target Rallying'.
    if (value) entries.push({ label: `${ex.radius ? 'Near' : 'Target'} ${exposureLabel(ex)}`, value });
  }
  const total = cappedToHit(entries, active);
  // No aiming and called shots are WORN-ONLY (2026-10-20): the Aim chip and the called-shot
  // penalty are settled before the map, so before anyone's auras are known; an aura's half
  // read made the roll drop Accurate while the Aim bonus still counted.
  const worn = active.filter((ex) => !ex.radius);
  return {
    total,
    entries,
    noAim: axisFlag(worn, 'snap'),
    calledShotReduction: Math.max(0, Math.min(20, axisTotal(worn, 'called'))),
  };
}

export interface ReactionExposure {
  /** ± to dodging the bearer's attacks. */
  dodgeMod: number;
  /** ± to parrying them. */
  parryMod: number;
  /** ± to the pinning tests the bearer's fire forces (v8.368.5). */
  pinMod: number;
  /** ± to the other side of a grapple with the bearer (v8.368.6). */
  escapeMod: number;
  /** What was worn and applied, for the notes. */
  labels: string[];
}

/**
 * THE REACTION FAMILY (v8.368.3): the bearer is the ATTACKER, the reader
 * the one reacting. Read once at the freeze and carried on the pending
 * defence as `attackerReactionMods`, so the banner, the first step, the
 * chain and the defence forecast all see the same number.
 */
export function reactionExposure(attackerBearer: ExposureBearer, defenderReader: ExposureReader): ReactionExposure {
  const active = activeExposures(attackerBearer, defenderReader);
  return {
    dodgeMod: axisTotal(active, 'dodge'),
    parryMod: axisTotal(active, 'parry'),
    pinMod: axisTotal(active, 'pin'),
    escapeMod: axisTotal(active, 'escape'),
    labels: active
      .filter((ex) => typeof ex.axes.dodge === 'number' || typeof ex.axes.parry === 'number')
      .map(exposureLabel),
  };
}

export interface AttackGate {
  /** The sentence that refuses the attack, or null. */
  refused: string | null;
  /** `autoHit`: the attack lands without a roll. */
  autoHit: boolean;
}

/**
 * Whether this attacker may attack the bearer at all (v8.368.2): `noMelee`,
 * `noRanged` and `maxRange` refuse with a sentence; `autoHit` is read
 * beside them because both live on the same roll.
 */
export function attackGate(bearer: ExposureBearer, reader: ExposureReader): AttackGate {
  const active = activeExposures(bearer, reader);
  let refused: string | null = null;
  if (reader.isMelee && axisFlag(active, 'noMelee')) refused = 'They cannot be reached in melee.';
  else if (!reader.isMelee && axisFlag(active, 'noRanged')) refused = 'They cannot be shot at.';
  else if (!reader.isMelee) {
    const max = axisFloor(active, 'maxRange');
    if (max > 0 && typeof reader.distanceM === 'number' && reader.distanceM > max) {
      refused = `They cannot be shot from beyond ${max} m - the target is ${Math.round(reader.distanceM)} m away.`;
    }
  }
  return { refused, autoHit: axisFlag(active, 'autoHit') };
}

/**
 * How many cover levels the bearer's worn exposures deny (Flushed out).
 * Cover is projected with the map, before anyone is known to be looking,
 * so EVERY scope applies: a `@hostile` Flushed out read through a team
 * check found no team and denied nothing (fixed 2026-10-20). A melee-only
 * one never applies (cover is a ranged matter), nor does an aura.
 */
export function coverLevelsDenied(bearer: Pick<ExposureBearer, 'conditions' | 'exposures'>): number {
  const worn = [...(bearer.exposures ?? []), ...(bearer.conditions ?? [])]
    .map(parseExposure)
    .filter((ex): ex is Exposure => !!ex && !ex.radius && ex.mode !== 'melee');
  return Math.max(0, axisTotal(worn, 'cover'));
}

/** The cover ladder, worst cover first. */
export const COVER_LADDER: readonly ('none' | 'low' | 'half' | 'threeQuarter' | 'full')[] = [
  'none',
  'low',
  'half',
  'threeQuarter',
  'full',
];

/** A cover level stepped DOWN by `steps`, never below none. */
export function stepCoverDown<L extends (typeof COVER_LADDER)[number]>(
  level: L,
  steps: number,
): (typeof COVER_LADDER)[number] {
  const idx = COVER_LADDER.indexOf(level);
  if (idx < 0 || steps <= 0) return level;
  return COVER_LADDER[Math.max(0, idx - steps)];
}

// ── The on-hit family ─────────────────────────────────────────

export interface OnHitExposure {
  /** ± damage per hit, before the typed bonus. */
  dmg: number;
  /** ± damage per hit of one damage type. */
  dmgByType: Record<'E' | 'I' | 'R' | 'X', number>;
  pen: number;
  ignoreTB: boolean;
  ignoreNatural: boolean;
  ignoreField: boolean;
  /** The lowest die face that triggers Righteous Fury against them (v8.368.1); null = the weapon's own. */
  furyThreshold: number | null;
  /** ± degrees of success on a hit. */
  dos: number;
  /** ± critical severity of hits on them. */
  crit: number;
  /** ± their Toughness Bonus as a hit sees it. */
  tb: number;
  /** No single hit deals more than this; 0 = no cap. */
  dmgCap: number;
  /** ± hits a volley lands. */
  hits: number;
  /** Attackers' weapons jam on this roll and up; 0 = the weapon's own. */
  jam: number;
  /** Attackers' weapons overheat on this roll and up, the Overheats rule (v8.371.1); 0 = only a weapon that Overheats, on its own roll. */
  overheat: number;
  /** +1: a missed attack may be re-rolled; −1: a hit must be; 0: neither (v8.368.5). */
  luck: -1 | 0 | 1;
  /** Every uncalled hit lands here; null = the dice decide (v8.368.5). */
  hitLocation: string | null;
  /** What was worn and applied, for the log. */
  labels: string[];
}

const ON_HIT_AXES: readonly string[] = [
  'dmg',
  'pen',
  'ignoreTB',
  'ignoreNatural',
  'ignoreField',
  'fury',
  'dos',
  'crit',
  'tb',
  'dmgCap',
  'hits',
  'jam',
  'overheat',
  'luck',
  'hitLocation',
];

/** The smallest positive value on an axis across the exposures, or 0. */
function axisFloor(exposures: readonly Exposure[], axis: NumericAxis): number {
  let floor = 0;
  for (const ex of exposures) {
    const v = ex.axes[axis];
    if (typeof v === 'number' && v > 0 && (floor === 0 || v < floor)) floor = v;
  }
  return floor;
}

/**
 * THE ON-HIT FAMILY, read inside `calcSingleHitDamage` - which has the
 * target already, so every caller (the roll and both forecasts) gets it
 * with no call site changed. The attacker's team is optional: absent,
 * a `@hostile` scope reads as applying.
 */
export function onHitExposure(bearer: ExposureBearer, reader: ExposureReader): OnHitExposure {
  const active = activeExposures(bearer, reader);
  const dmgByType = { E: 0, I: 0, R: 0, X: 0 } as Record<'E' | 'I' | 'R' | 'X', number>;
  for (const t of ['E', 'I', 'R', 'X'] as const) dmgByType[t] = axisTotal(active, `dmg:${t}`);
  return {
    dmg: axisTotal(active, 'dmg'),
    dmgByType,
    pen: axisTotal(active, 'pen'),
    ignoreTB: axisFlag(active, 'ignoreTB'),
    ignoreNatural: axisFlag(active, 'ignoreNatural'),
    ignoreField: axisFlag(active, 'ignoreField'),
    furyThreshold: ((f) => (f ? Math.max(2, Math.min(10, f)) : null))(axisFloor(active, 'fury')),
    dos: axisTotal(active, 'dos'),
    crit: axisTotal(active, 'crit'),
    tb: axisTotal(active, 'tb'),
    dmgCap: axisFloor(active, 'dmgCap'),
    hits: axisTotal(active, 'hits'),
    jam: axisFloor(active, 'jam'),
    overheat: axisFloor(active, 'overheat'),
    luck: Math.sign(axisTotal(active, 'luck')) as -1 | 0 | 1,
    hitLocation: active.map((ex) => ex.axes.hitLocation).find((v): v is string => typeof v === 'string' && !!v) ?? null,
    labels: active
      .filter((ex) => (Object.keys(ex.axes) as Axis[]).some((a) => a.startsWith('dmg:') || ON_HIT_AXES.includes(a)))
      .map(exposureLabel),
  };
}

/** The attack's degrees of success after the bearer's `dos` exposures, never below one. */
export function exposedDos(dos: number, bearer: ExposureBearer, reader: ExposureReader): number {
  const shift = axisTotal(activeExposures(bearer, reader), 'dos');
  return shift ? Math.max(1, dos + shift) : dos;
}

/**
 * Spend `#uses` on the exposures a burst of `hits` landed on. Each hit
 * consumes one use of every exposure that has them; an exposure spent
 * to zero is gone. Returns a NEW list; codes without uses are untouched.
 * An AURA (a code with a radius) is not worn against the blow - it is
 * read by whoever stands near its bearer - so hits on the bearer never
 * spend it.
 */
export function consumeExposureUses(conditions: readonly string[], hits: number): string[] {
  if (hits <= 0) return [...conditions];
  return conditions.flatMap((code) => spendUses(code, hits));
}

/** One code after `hits` landed: unchanged without uses, gone when spent, else with fewer left. */
function spendUses(code: string, hits: number): string[] {
  const ex = parseExposure(code);
  if (!ex || !ex.uses || ex.radius) return [code];
  const left = ex.uses - hits;
  return left <= 0 ? [] : [buildExposureCode({ ...ex, uses: left })];
}

// ── The gating flags ──────────────────────────────────────────

/** `sanctioned`: allies cannot target them. True when the reader is on the bearer's own team. */
export function sanctionedAgainst(bearer: ExposureBearer, reader: ExposureReader): boolean {
  if (!axisFlag(activeExposures(bearer, reader), 'sanctioned')) return false;
  return effectReaches('allies', sideOfParty(bearer), sideOfParty(reader));
}

/** `taunt`: an enemy who can target them must - a reminder for the reader, never a rule. Strict teams (v8.368.0). */
export function tauntsReader(bearer: ExposureBearer, reader: ExposureReader): boolean {
  if (!axisFlag(activeExposures(bearer, reader), 'taunt')) return false;
  return effectReaches('hostiles', sideOfParty(bearer), sideOfParty(reader));
}
